import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { UserReadDTO, UserCreateDTO, UserRole } from '../generated/api-client';
import { CrudDataGridPage, CrudConfig, CrudField } from '../components/CrudDataGrid';
import { GridRowModel } from '@mui/x-data-grid';
import PersonIcon from '@mui/icons-material/Person';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { ActionButton } from '../components/common';
import { apiClient } from '../services/apiClient';

export const UsersPage: React.FC = () => {
  const { inviteUser, user } = useAuth();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.BaseUser);
  const [inviteLoading, setInviteLoading] = useState(false);

  // Verifica se l'utente ha i permessi per vedere questa pagina
  const hasPermission = user?.role === 'admin' || user?.role === 'mantainer';

  const getRoleLabel = (role?: UserRole) => {
    if (!role) return 'N/A';
    const roleMap: { [key: string]: string } = {
      baseUser: 'Utente Base',
      mantainer: 'Mantainer',
      admin: 'Amministratore',
    };
    return roleMap[role] || role;
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Non hai i permessi per accedere a questa sezione. Solo gli amministratori e i mantainer
          possono gestire gli utenti.
        </Alert>
      </Box>
    );
  }

  const fields: CrudField[] = [
    {
      field: 'firstname',
      headerName: 'Nome',
      width: 200,
      editable: true,
    },
    {
      field: 'lastname',
      headerName: 'Cognome',
      width: 200,
      editable: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      editable: true,
    },
    {
      field: 'role',
      headerName: 'Ruolo',
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: UserRole.BaseUser, label: getRoleLabel(UserRole.BaseUser) },
        { value: UserRole.Mantainer, label: getRoleLabel(UserRole.Mantainer) },
        { value: UserRole.Admin, label: getRoleLabel(UserRole.Admin) },
      ],
    },
    {
      field: 'is_active',
      headerName: 'Attivo',
      width: 100,
      editable: true,
      type: 'boolean',
    },
    {
      field: 'is_verified',
      headerName: 'Verificato',
      width: 100,
      editable: false,
      type: 'boolean',
    },
  ];

  const handleOpenInviteDialog = () => {
    setInviteDialogOpen(true);
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteEmail('');
    setInviteRole(UserRole.BaseUser);
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Inserisci un'email");
      return;
    }

    setInviteLoading(true);
    try {
      await inviteUser(inviteEmail.trim(), inviteRole);
      toast.success(`Invito inviato a ${inviteEmail}`);
      handleCloseInviteDialog();
      // Ricarica la lista utenti
      window.location.reload();
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || "Errore nell'invio dell'invito";
      toast.error(errorMsg);
    } finally {
      setInviteLoading(false);
    }
  };

  const config: CrudConfig<UserReadDTO, UserCreateDTO> = {
    queryKey: 'users',
    fields,
    listFn: async () => {
      return await apiClient.list_items_user__get();
    },
    createFn: async (data: UserCreateDTO) => {
      // Non permettere creazione diretta, solo tramite invito
      throw new Error('La creazione utenti è disponibile solo tramite invito');
    },
    updateFn: async (id: string, data: UserCreateDTO) => {
      return await apiClient.update_item_user__item_id__put(id, data);
    },
    deleteFn: async (id: string) => {
      await apiClient.delete_item_user__item_id__delete(id);
    },
    getId: (row: UserReadDTO) => row.id || '',
    getCreateDto: (row: GridRowModel) => {
      return new UserCreateDTO({
        firstname: row.firstname || '',
        lastname: row.lastname || '',
        email: row.email || '',
        role: row.role,
        is_active: row.is_active,
      });
    },
  };

  return (
    <Box sx={{ height: '100%' }}>
      <CrudDataGridPage
        title="Utenti"
        config={config}
        icon={<PersonIcon fontSize="large" />}
        actions={
          <ActionButton
            icon={<PersonAddIcon />}
            tooltip="Invita Utente"
            onClick={handleOpenInviteDialog}
          />
        }
      />

      {/* Dialog per invitare utenti */}
      <Dialog open={inviteDialogOpen} onClose={handleCloseInviteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Invita Nuovo Utente</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="nuovo.utente@example.com"
              fullWidth
              required
            />
            <TextField
              select
              label="Ruolo"
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value as UserRole)}
              fullWidth
              required
            >
              <MenuItem value={UserRole.BaseUser}>{getRoleLabel(UserRole.BaseUser)}</MenuItem>
              <MenuItem value={UserRole.Mantainer}>{getRoleLabel(UserRole.Mantainer)}</MenuItem>
              <MenuItem value={UserRole.Admin}>{getRoleLabel(UserRole.Admin)}</MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              L'utente riceverà un'email con un link per completare la registrazione.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ pb: 2, pr: 2 }}>
            <Button onClick={handleCloseInviteDialog} disabled={inviteLoading}>
              Annulla
            </Button>
            <Button
              onClick={handleInviteUser}
              variant="contained"
              disabled={inviteLoading || !inviteEmail.trim()}
              sx={{ ml: 2 }}
            >
              {inviteLoading ? <CircularProgress size={24} /> : 'Invia Invito'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
